import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Notification, NotificationContextType } from '../types/notification';
import { useAuth } from './AuthContext';
import { useTenantData } from './TenantDataContext';
import { signalRNotificationService, MenuCounts } from '../services/signalRNotificationService';
import { NotificationService } from '../api/services/NotificationService';
import type {
  Routemize_DTOs_NotificationSummaryDTO,
  Routemize_DTOs_MarkAsReadDTO,
  Routemize_DTOs_BulkDeleteNotificationDTO,
} from '../api';

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

// Map backend notification type enum to frontend type
// Exported for unit tests (NotificationContext.test.ts).
export const mapBackendNotificationType = (backendType?: string): Notification['type'] => {
  if (!backendType) return 'system';
  switch (backendType) {
    case 'Appointment':
    case 'AppointmentCreated':
      return 'new_appointment';
    case 'AppointmentUpdated':
      return 'appointment_rescheduled';
    case 'AppointmentCancelled':
      return 'appointment_cancelled';
    case 'Request':
    case 'ContactCaptured':
      return 'new_request';
    case 'VoiceMessage':
      return 'voice_message';
    case 'Reminder':
      return 'reminder';
    case 'Alert':
      return 'system';
    case 'Contact':
      return 'system';
    case 'System':
    default:
      return 'system';
  }
};

// Map backend priority enum to frontend priority
const mapBackendPriority = (backendPriority?: string): Notification['priority'] => {
  if (!backendPriority) return 'medium';
  switch (backendPriority) {
    case 'Low': return 'low';
    case 'Normal': return 'medium';
    case 'High': return 'high';
    case 'Critical': return 'high';
    default: return 'medium';
  }
};

// Map backend DTO to frontend interface
// Exported for unit tests (NotificationContext.test.ts).
export const mapNotificationDTO = (dto: Routemize_DTOs_NotificationSummaryDTO, tenantTimezone?: string): Notification => {
  // Variables is already a parsed object from the backend (Dictionary<string, object>).
  // The .NET serializer can produce PascalCase or camelCase keys depending on the
  // notification provider — use a case-insensitive lookup so we don't silently miss
  // `AppointmentId` vs `appointmentId` (which previously caused notifications to
  // fall through to the generic "View bookings" link instead of opening the
  // specific appointment).
  let metadata: Notification['metadata'] = {};
  try {
    if (dto.variables) {
      const lookup = new Map<string, any>();
      for (const [k, v] of Object.entries(dto.variables)) {
        if (v != null && v !== '') lookup.set(k.toLowerCase(), v);
      }
      const pick = (...keys: string[]): any => {
        for (const k of keys) {
          const v = lookup.get(k.toLowerCase());
          if (v != null && v !== '') return v;
        }
        return undefined;
      };
      const firstName = pick('customerFirstName', 'firstName');
      const lastName = pick('customerLastName', 'lastName', 'surName');
      const composedName = [firstName, lastName].filter(Boolean).join(' ') || undefined;

      metadata = {
        appointmentId: pick('appointmentId', 'entityId', 'id'),
        requestId: pick('requestId', 'request_id'),
        customerId: pick('customerId'),
        customerName: pick('customerName', 'customer') || composedName,
        customerPhone: pick('customerPhone', 'phone'),
        customerEmail: pick('customerEmail', 'email'),
        customerAddress: pick('customerAddress', 'address'),
        appointmentDate: pick('appointmentDate', 'date'),
        appointmentTime: pick('appointmentTime', 'time'),
        serviceName: pick('serviceName', 'service', 'eventTypeName', 'eventName'),
        assignedBy: pick('assignedBy'),
        formName: pick('formName', 'bookingFormName'),
        cancellationReason: pick('cancellationReason'),
        previousDate: pick('previousDate'),
        previousTime: pick('previousTime'),
        isRoutemize: pick('isRoutemize'),
        // VoiceMessage: the ETO's top-level ids. ContactId is absent when the caller
        // didn't match a contact; getNotificationLink also ignores Guid.Empty.
        contactId: pick('contactId'),
        voiceCallId: pick('voiceCallId'),
      };
    }
  } catch (error) {
    // Failed to extract notification variables
  }

  const rawTitle = dto.title || '';
  const rawMessage = dto.description || '';
  const notifType = mapBackendNotificationType(dto.notificationType as unknown as string);

  // Extract customer name from "Meeting with - Name" if metadata is empty
  const meetingNameMatch = rawTitle.match(/Meeting with\s*-?\s*(.+)$/i);
  if (meetingNameMatch && !metadata.customerName) {
    metadata.customerName = meetingNameMatch[1].trim();
  }

  // Try to extract entity ID from actionUrl as fallback
  if (dto.actionUrl) {
    if (!metadata.appointmentId) {
      const apptMatch = dto.actionUrl.match(/appointmentId=([a-f0-9-]+)/i) || dto.actionUrl.match(/appointments?\/([a-f0-9-]+)/i);
      if (apptMatch) metadata.appointmentId = apptMatch[1];
    }
    if (!metadata.requestId) {
      const reqMatch = dto.actionUrl.match(/requestId=([a-f0-9-]+)/i) || dto.actionUrl.match(/requests?\/([a-f0-9-]+)/i);
      if (reqMatch) metadata.requestId = reqMatch[1];
    }
  }

  let title = rawTitle;
  const typePrefix = rawTitle.match(/^(New Appointment|New Request|Appointment Updated|Appointment Rescheduled|Appointment Cancelled):\s*/i);
  if (typePrefix) {
    const afterPrefix = rawTitle.slice(typePrefix[0].length).trim();
    const displayName = metadata.serviceName || metadata.formName;
    if (displayName) {
      title = `${typePrefix[1]}: ${displayName}`;
    } else if (afterPrefix.match(/^Meeting with/i) && metadata.customerName) {
      title = `${typePrefix[1]}: ${metadata.customerName}`;
    }
  }

  let message = rawMessage;
  const betterName = metadata.serviceName || metadata.formName;
  if (betterName) {
    message = message.replace(/(?:appointment|meeting)\s+'[^']+'/gi, `appointment '${betterName}'`);
  } else if (metadata.customerName) {
    message = message.replace(
      /(?:appointment|meeting)\s+'Meeting with\s*-?\s*[^']+'/gi,
      `appointment for ${metadata.customerName}`
    );
  }
  message = message.replace(
    /(\d{4}-\d{2}-\d{2})\s+(\d{1,2}:\d{2})\s+at\s+(\d{1,2}:\d{2}\s*(?:AM|PM)?)/gi,
    (_match, datePart, _timePart, ampmTime) => {
      const d = new Date(datePart + 'T12:00:00');
      if (isNaN(d.getTime())) return _match;
      return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', ...(tenantTimezone ? { timeZone: tenantTimezone } : {}) }) + ` at ${ampmTime}`;
    }
  ).replace(
    /(\d{4}-\d{2}-\d{2})/g,
    (_match, datePart) => {
      const d = new Date(datePart + 'T12:00:00');
      if (isNaN(d.getTime())) return _match;
      return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', ...(tenantTimezone ? { timeZone: tenantTimezone } : {}) });
    }
  );

  return {
    id: dto.id!,
    type: notifType,
    title,
    message,
    timestamp: new Date(dto.creationTime!),
    isRead: dto.isRead || false,
    priority: mapBackendPriority(dto.priority as unknown as string),
    actionUrl: dto.actionUrl,
    source: dto.source ?? undefined,
    metadata
  };
};

const NOTIFICATIONS_LAST_SEEN_KEY = 'routemize_notifications_last_seen';
const BOOKINGS_LAST_SEEN_KEY = 'routemize_bookings_last_seen';
const BOOKINGS_LAST_SEEN_COUNT_KEY = 'routemize_bookings_last_seen_count';
const BOOKINGS_LAST_SEEN_DATE_KEY = 'routemize_bookings_last_seen_date';

const getSavedBookingsCount = () => {
  if (typeof window === 'undefined') return 0;
  const storedDate = window.localStorage.getItem(BOOKINGS_LAST_SEEN_DATE_KEY);
  const today = new Date().toDateString();
  // If the saved date is not today, reset — it's a fresh day
  if (storedDate !== today) return 0;
  const storedCount = window.localStorage.getItem(BOOKINGS_LAST_SEEN_COUNT_KEY);
  const parsed = storedCount ? parseInt(storedCount, 10) : NaN;
  return Number.isFinite(parsed) ? parsed : 0;
};

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { companyTimezone } = useTenantData();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [menuCounts, setMenuCounts] = useState<MenuCounts | null>(null);
  const [requestsBadgeCount, setRequestsBadgeCount] = useState(0);
  const [notificationsBadgeCount, setNotificationsBadgeCount] = useState(0);
  const [bookingsBadgeCount, setBookingsBadgeCount] = useState(0);
  const [lastSeenBookingsCount, setLastSeenBookingsCount] = useState(getSavedBookingsCount);
  const [hasSeenNotifications, setHasSeenNotifications] = useState(false);
  const [hasSeenBookings, setHasSeenBookings] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showOnlyUnread, setShowOnlyUnread] = useState(false);
  const [connectionLost, setConnectionLost] = useState(false);
  const latestMenuCountUpdateMsRef = React.useRef(0);
  const lastSeenBookingsCountRef = React.useRef(lastSeenBookingsCount);
  const fetchGenRef = React.useRef(0);
  const hasLoadedRef = React.useRef(false);

  // Fetch notifications from API
  // A plain refresh (no paging/filter overrides) always issues the identical
  // query, so concurrent callers — the dropdown opening, a SignalR push and a
  // menu-count update can land in the same tick — share the in-flight request
  // instead of stacking N identical round trips. Awaiting callers still await
  // real completion.
  //
  // `force` is for refetches that follow our own write (mark-all-read, clear):
  // joining a request issued before the write would resolve with pre-write data.
  const plainRefreshInFlight = React.useRef<Promise<void> | null>(null);

  const fetchNotifications = (params?: {
    skipCount?: number;
    maxResultCount?: number;
    isRead?: boolean;
    silentRefresh?: boolean; // Add flag for background updates
    force?: boolean;
  }): Promise<void> => {
    if (!isAuthenticated) return Promise.resolve();

    const isPlainRefresh =
      !params?.force &&
      params?.skipCount === undefined &&
      params?.maxResultCount === undefined &&
      params?.isRead === undefined;

    if (!isPlainRefresh) return runFetchNotifications(params);

    if (plainRefreshInFlight.current) return plainRefreshInFlight.current;

    const run = runFetchNotifications(params).finally(() => {
      plainRefreshInFlight.current = null;
    });
    plainRefreshInFlight.current = run;
    return run;
  };

  const runFetchNotifications = async (params?: {
    skipCount?: number;
    maxResultCount?: number;
    isRead?: boolean;
    silentRefresh?: boolean;
    force?: boolean;
  }) => {
    const gen = ++fetchGenRef.current;
    const silent = !!params?.silentRefresh;

    // Only show loading state for user-initiated refreshes. Background /
    // dropdown-open refreshes must not flip `loading` — when the list is
    // empty that swap is what made the empty state flash to a skeleton.
    if (!silent) {
      setLoading(true);
      setError(null);
    }

    try {
      const result = await NotificationService.getApiAppNotificationGetUserNotifications(
        showOnlyUnread ? false : params?.isRead,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        params?.skipCount || 0,
        params?.maxResultCount || 50
      );

      if (gen !== fetchGenRef.current) return;

      const mappedNotifications = (result.items as unknown as Routemize_DTOs_NotificationSummaryDTO[] || []).map(dto => mapNotificationDTO(dto, companyTimezone));

      // Filter out read notifications if showOnlyUnread is true
      const filteredNotifications = showOnlyUnread
        ? mappedNotifications.filter((n: Notification) => !n.isRead)
        : mappedNotifications;

      // Smart merge: preserve existing objects when only isRead changed
      setNotifications(prev => {
        return filteredNotifications.map((newNotif: Notification) => {
          const existing = prev.find(p => p.id === newNotif.id);
          // If notification exists and only isRead changed, reuse the object
          if (existing &&
              existing.title === newNotif.title &&
              existing.message === newNotif.message &&
              existing.timestamp.getTime() === newNotif.timestamp.getTime()) {
            // Only update if isRead actually changed
            if (existing.isRead !== newNotif.isRead) {
              return { ...existing, isRead: newNotif.isRead };
            }
            return existing; // Return exact same object reference
          }
          return newNotif; // New or modified notification
        });
      });
      setError(null);
    } catch (error) {
      if (gen !== fetchGenRef.current) return;
      // Keep whatever we already have. Clearing the list on a failed
      // (especially silent/SignalR) refresh is what made the empty state
      // flash in and out whenever the connection hiccuped.
      if (!silent || !hasLoadedRef.current) {
        setError('Failed to load notifications');
      }
    } finally {
      if (gen === fetchGenRef.current) {
        setLoading(false);
        hasLoadedRef.current = true;
        setHasLoaded(true);
      }
    }
  };

  // Fetch unread count
  const fetchUnreadCount = async () => {
    if (!isAuthenticated) return;
    
    try {
      const count = await NotificationService.getApiAppNotificationGetUnreadCount();
      setUnreadCount(count || 0);
    } catch (error) {
      // Failed to fetch unread count
      // Fallback to counting unread in current notifications
      setUnreadCount(notifications.filter(n => !n.isRead).length);
    }
  };

  // Mark notification as read with API call.
  // Announcements use announcementIds; regular notifications use notificationIds.
  const markAsRead = async (id: string) => {
    try {
      const isAnnouncement = notifications.find(n => n.id === id)?.source === 'announcement';
      const request: Routemize_DTOs_MarkAsReadDTO = isAnnouncement
        ? { announcementIds: [id], markAll: false }
        : { notificationIds: [id], markAll: false };
      await NotificationService.postApiAppNotificationMarkAsRead(request);
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, isRead: true } : n)
      );
      
      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      // Failed to mark as read
      
      // Fallback to local state update only
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, isRead: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  // Mark all notifications as read with API call
  const markAllAsRead = async () => {
    try {
      const request: Routemize_DTOs_MarkAsReadDTO = {
        notificationIds: [],
        markAll: true
      };
      await NotificationService.postApiAppNotificationMarkAsRead(request);
      
      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
      setNotificationsBadgeCount(0);
      setHasSeenNotifications(true);
      await fetchNotifications({ silentRefresh: true, force: true });
      await fetchUnreadCount();
    } catch (error) {
      // Failed to mark all as read

      // Fallback to local state update only
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
      setNotificationsBadgeCount(0);
      setHasSeenNotifications(true);
    }
  };

  const clearAllNotifications = async () => {
    try {
      // Backend currently supports mark-all-read, not hard-delete.
      const request: Routemize_DTOs_MarkAsReadDTO = {
        notificationIds: [],
        markAll: true
      };
      await NotificationService.postApiAppNotificationMarkAsRead(request);

      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
      setShowOnlyUnread(false);
      setHasSeenNotifications(true);
      setNotificationsBadgeCount(0);
      localStorage.setItem(NOTIFICATIONS_LAST_SEEN_KEY, Date.now().toString());
      await fetchNotifications({ silentRefresh: true, force: true });
      await fetchUnreadCount();
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    }
  };

  // Dismiss (hard-remove) a single notification or announcement.
  // Announcements route to announcementIds; regular notifications to notificationIds.
  const dismissNotification = async (id: string) => {
    const isAnnouncement = notifications.find(n => n.id === id)?.source === 'announcement';
    const request: Routemize_DTOs_BulkDeleteNotificationDTO = isAnnouncement
      ? { announcementIds: [id] }
      : { notificationIds: [id] };
    await NotificationService.postApiAppNotificationBulkDelete(request);
    setNotifications(prev => prev.filter(n => n.id !== id));
    setUnreadCount(prev => {
      const wasUnread = notifications.find(n => n.id === id && !n.isRead);
      return wasUnread ? Math.max(0, prev - 1) : prev;
    });
  };

  // Dismiss multiple notifications/announcements in a single bulkDelete call.
  // Splits ids by source so announcements go to announcementIds and regular
  // notifications go to notificationIds.
  const dismissNotifications = async (ids: string[]) => {
    if (ids.length === 0) return;
    const announcementIds: string[] = [];
    const notificationIds: string[] = [];
    for (const id of ids) {
      const n = notifications.find(n => n.id === id);
      if (n?.source === 'announcement') {
        announcementIds.push(id);
      } else {
        notificationIds.push(id);
      }
    }
    const request: Routemize_DTOs_BulkDeleteNotificationDTO = {
      ...(notificationIds.length > 0 ? { notificationIds } : {}),
      ...(announcementIds.length > 0 ? { announcementIds } : {}),
    };
    await NotificationService.postApiAppNotificationBulkDelete(request);
    const idSet = new Set(ids);
    const removedUnreadCount = notifications.filter(n => idSet.has(n.id) && !n.isRead).length;
    setNotifications(prev => prev.filter(n => !idSet.has(n.id)));
    setUnreadCount(prev => Math.max(0, prev - removedUnreadCount));
  };

  const addNotification = (notificationData: Omit<Notification, 'id' | 'timestamp'>) => {
    // This would typically be handled by the backend notification system
    const newNotification: Notification = {
      ...notificationData,
      id: Date.now().toString(),
      timestamp: new Date()
    };
    setNotifications(prev => [newNotification, ...prev]);
    if (!newNotification.isRead) {
      setUnreadCount(prev => prev + 1);
    }
  };

  // Mark notifications as seen - called when user visits the Notifications page
  // This clears the badge count until new updates arrive
  const markNotificationsAsSeen = React.useCallback(() => {
    setHasSeenNotifications(true);
    setNotificationsBadgeCount(0);
    // Store the timestamp of when notifications were last viewed
    localStorage.setItem(NOTIFICATIONS_LAST_SEEN_KEY, Date.now().toString());
  }, []);

  // Mark bookings as seen - called when user visits the Bookings page
  // This clears the badge count until new appointments arrive
  const markBookingsAsSeen = React.useCallback(() => {
    const todaysCount = menuCounts?.todaysAppointments ?? lastSeenBookingsCountRef.current ?? 0;
    setHasSeenBookings(true);
    setBookingsBadgeCount(0);
    setLastSeenBookingsCount(todaysCount);
    lastSeenBookingsCountRef.current = todaysCount;
    const today = new Date().toDateString();
    localStorage.setItem(BOOKINGS_LAST_SEEN_KEY, Date.now().toString());
    localStorage.setItem(BOOKINGS_LAST_SEEN_COUNT_KEY, todaysCount.toString());
    localStorage.setItem(BOOKINGS_LAST_SEEN_DATE_KEY, today);
  }, [menuCounts?.todaysAppointments]);

  useEffect(() => {
    lastSeenBookingsCountRef.current = lastSeenBookingsCount;
  }, [lastSeenBookingsCount]);

  // Fetch as soon as we know the user is authenticated. Do not skip the
  // first run when `isAuthenticated` is already true (the previous
  // wasAuthenticated-ref guard did, which left the inbox empty until the
  // user opened the dropdown and triggered a refresh — and that refresh
  // then flashed a skeleton over the empty state).
  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications();
      fetchUnreadCount();
      return;
    }
    fetchGenRef.current += 1; // invalidate in-flight fetches from the previous session
    hasLoadedRef.current = false;
    setHasLoaded(false);
    setLoading(false);
    setError(null);
    setNotifications([]);
    setUnreadCount(0);
    setMenuCounts(null);
    setShowOnlyUnread(false);
  }, [isAuthenticated]);

  // Initialize SignalR connection and event handlers
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    let onNotificationsChanged: (() => void) | null = null;
    let onMenuCountsUpdate: ((counts: MenuCounts) => void) | null = null;
    let onMenuCountsRefreshNeeded: (() => void) | null = null;

    const initializeSignalR = async () => {
      try {
        // Initialize SignalR connection
        await signalRNotificationService.initialize();

        // Register the connection-lost callback so the UI can react when all
        // reconnect attempts have been exhausted or the connection closes cleanly.
        signalRNotificationService.setOnConnectionLost(() => {
          setConnectionLost(true);
        });

        // Set up event handlers
        // When notifications change, refetch from API for consistency
        onNotificationsChanged = () => {
          // Use setTimeout to ensure this runs in the next tick
          // This helps with any potential closure issues
          setTimeout(() => {
            fetchNotifications({ silentRefresh: true }); // Silent refresh - no loading state
            fetchUnreadCount();
          }, 0);
        };
        signalRNotificationService.setOnNotificationsChanged(onNotificationsChanged);

        onMenuCountsUpdate = (counts: MenuCounts) => {
          const incomingUpdateMs = counts?.lastUpdated ? new Date(counts.lastUpdated).getTime() : Date.now();
          if (incomingUpdateMs < latestMenuCountUpdateMsRef.current) {
            return;
          }
          latestMenuCountUpdateMsRef.current = incomingUpdateMs;

          // Connection is active — clear any prior lost-connection state
          setConnectionLost(false);
          setMenuCounts(counts);
          // Update unread notifications count from menu counts
          if (counts.unreadNotifications !== undefined) {
            setUnreadCount(prevUnread => {
              if (counts.unreadNotifications > prevUnread) {
                setTimeout(() => {
                  fetchNotifications({ silentRefresh: true });
                }, 0);
              }
              return counts.unreadNotifications;
            });
            const isOnNotificationsPage = window.location.pathname.startsWith('/notifications');
            if (isOnNotificationsPage) {
              setHasSeenNotifications(true);
              setNotificationsBadgeCount(0);
            } else {
              setHasSeenNotifications(false);
              setNotificationsBadgeCount(counts.unreadNotifications);
            }
          }
          // Requests badge always mirrors the last-24-hours count shown on the Requests
          // page. It is NOT cleared when the page is viewed.
          if (counts.requestsLast24Hours !== undefined) {
            setRequestsBadgeCount(counts.requestsLast24Hours);
          }
          // Update bookings badge count - show only new appointments since last visit
          if (counts.todaysAppointments !== undefined) {
            const isOnBookingsPage = window.location.pathname.startsWith('/bookings');
            if (isOnBookingsPage) {
              // Auto-clear badge and save current count while user is on the page
              setHasSeenBookings(true);
              setBookingsBadgeCount(0);
              const today = new Date().toDateString();
              lastSeenBookingsCountRef.current = counts.todaysAppointments;
              setLastSeenBookingsCount(counts.todaysAppointments);
              localStorage.setItem(BOOKINGS_LAST_SEEN_COUNT_KEY, counts.todaysAppointments.toString());
              localStorage.setItem(BOOKINGS_LAST_SEEN_DATE_KEY, today);
            } else {
              // Day boundary: if saved date is not today, reset baseline to 0
              const savedDate = localStorage.getItem(BOOKINGS_LAST_SEEN_DATE_KEY);
              const today = new Date().toDateString();
              if (savedDate !== today) {
                lastSeenBookingsCountRef.current = 0;
              }
              setHasSeenBookings(false);
              const unseenBookings = Math.max(0, counts.todaysAppointments - lastSeenBookingsCountRef.current);
              setBookingsBadgeCount(unseenBookings);
            }
          }
        };
        signalRNotificationService.setOnMenuCountsUpdate(onMenuCountsUpdate);

        onMenuCountsRefreshNeeded = () => {
          signalRNotificationService.refreshCounts().catch(() => {
            // Ignore errors — SignalR may not be fully ready yet
          });
        };
        signalRNotificationService.setOnMenuCountsRefreshNeeded(onMenuCountsRefreshNeeded);

        // Request initial menu counts from backend via SignalR
        await signalRNotificationService.refreshCounts();
      } catch (error) {
        // SignalR failure (e.g. hub returning 404, websocket blocked) means
        // we won't get real-time updates — but the REST fetch above still
        // works, so we must NOT set the main `error` state here. Doing so
        // makes the notifications dropdown show the catastrophic
        // "Couldn't load notifications" error even when notifications
        // loaded successfully. The `connectionLost` flag (raised by
        // setOnConnectionLost when reconnect attempts are exhausted) is
        // the proper signal for "real-time updates unavailable" and the
        // dropdown already renders a dedicated banner for it.
        console.warn('Real-time notifications unavailable:', error);
        setConnectionLost(true);
      }
    };

    initializeSignalR();

    return () => {
      signalRNotificationService.disconnect();

      if (onNotificationsChanged) {
        signalRNotificationService.removeOnNotificationsChanged(onNotificationsChanged);
      }
      if (onMenuCountsUpdate) {
        signalRNotificationService.removeOnMenuCountsUpdate(onMenuCountsUpdate);
      }
      if (onMenuCountsRefreshNeeded) {
        signalRNotificationService.removeOnMenuCountsRefreshNeeded(onMenuCountsRefreshNeeded);
      }
    };
  }, [isAuthenticated]);

  // Every callback below is handed out with a permanently stable identity.
  //
  // Consumers put these in `useEffect` dependency arrays. A fresh closure per
  // provider render makes those effects re-run on every render, and because the
  // fetch itself sets state, that is an unbounded fetch->render->fetch loop
  // bounded only by network round-trip time — it is what left the notification
  // dropdown stuck on its skeleton. The implementations still close over live
  // state; they are reached through a ref repointed each render, so callers
  // always invoke the current one.
  const latestHandlers = React.useRef({
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    clearAllNotifications,
    dismissNotification,
    dismissNotifications,
    addNotification,
  });
  latestHandlers.current = {
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    clearAllNotifications,
    dismissNotification,
    dismissNotifications,
    addNotification,
  };

  const stableRefreshNotifications = React.useCallback(
    (options?: { silent?: boolean }) => {
      setShowOnlyUnread(false); // Reset filter when manually refreshing
      return latestHandlers.current.fetchNotifications({ silentRefresh: options?.silent });
    }, []);
  const stableMarkAsRead = React.useCallback(
    (id: string) => latestHandlers.current.markAsRead(id), []);
  const stableMarkAllAsRead = React.useCallback(
    () => latestHandlers.current.markAllAsRead(), []);
  const stableClearAllNotifications = React.useCallback(
    () => latestHandlers.current.clearAllNotifications(), []);
  const stableDismissNotification = React.useCallback(
    (id: string) => latestHandlers.current.dismissNotification(id), []);
  const stableDismissNotifications = React.useCallback(
    (ids: string[]) => latestHandlers.current.dismissNotifications(ids), []);
  const stableAddNotification = React.useCallback(
    (data: Omit<Notification, 'id' | 'timestamp'>) =>
      latestHandlers.current.addNotification(data), []);

  const contextValue = React.useMemo(
    () => ({
      notifications,
      unreadCount,
      menuCounts,
      requestsBadgeCount,
      notificationsBadgeCount,
      loading,
      hasLoaded,
      error,
      connectionLost,
      markAsRead: stableMarkAsRead,
      markAllAsRead: stableMarkAllAsRead,
      clearAllNotifications: stableClearAllNotifications,
      dismissNotification: stableDismissNotification,
      dismissNotifications: stableDismissNotifications,
      addNotification: stableAddNotification,
      refreshNotifications: stableRefreshNotifications,
      markNotificationsAsSeen,
      bookingsBadgeCount,
      markBookingsAsSeen,
    }),
    [
      notifications,
      unreadCount,
      menuCounts,
      requestsBadgeCount,
      notificationsBadgeCount,
      loading,
      hasLoaded,
      error,
      connectionLost,
      stableMarkAsRead,
      stableMarkAllAsRead,
      stableClearAllNotifications,
      stableDismissNotification,
      stableDismissNotifications,
      stableAddNotification,
      stableRefreshNotifications,
      markNotificationsAsSeen,
      bookingsBadgeCount,
      markBookingsAsSeen,
    ],
  );

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};
